export type BillingFrequency = 'monthly' | 'yearly';

export type SubscriptionPlan =
  | 'Free'
  | 'Proficient Pioneer'
  | 'Legendary Explorer'
  | 'Galactic Adventurer'
  | 'Nexus Elite';

export type SubscriptionPlans = {
  [P in SubscriptionPlan]: {
    id: number;
    price: {
      monthly: number;
      yearly: number;
      yearlyOriginal?: number;
    };
    info: { highlighted?: string; normal: string }[];
    features: { isAvailable: boolean; label: string }[];
    iconSrc: string;
    iconSrcLarge: string;
    accentColor: string;
    isMostPopular?: boolean;
  };
};

export const DEFAULT_PLAN_ID = 1;
export const thankYouPageTitle = 'Successful Payment | EvoLux';

export const SUBSCRIPTION_PLANS_CONFIG: SubscriptionPlans = {
  Free: {
    id: DEFAULT_PLAN_ID,
    price: { monthly: 0, yearly: 0 },
    info: [
      { highlighted: '$0', normal: 'Deposit Required' },
      { normal: 'No Custom Charter Hourly Rate Discount' },
      { normal: 'No One-Way Charter Special Discount' },
    ],
    features: [
      {
        isAvailable: true,
        label: 'Join SKYSHARE Groups and SKYSHARE Group Reservations',
      },
      { isAvailable: true, label: 'Get One-Way Special alerts' },
      { isAvailable: true, label: 'Search One-Way Special routes' },
      {
        isAvailable: false,
        label: 'Create SKYSHARE GROUPS and SKYSHARE Group Reservations',
      },
      {
        isAvailable: false,
        label: 'Full-Service Private Travel Advisor Team & Concierge Services',
      },
      {
        isAvailable: false,
        label: 'Member Welcoming Kit',
      },
      {
        isAvailable: false,
        label: 'Member Card',
      },
      {
        isAvailable: false,
        label: 'Invites to Parties & Special Events',
      },
      {
        isAvailable: false,
        label: 'Invites to Exclusive Member Only Parties & Private Events',
      },
      {
        isAvailable: false,
        label: 'Exclusive SKYSHARE GROUPS',
      },
      {
        isAvailable: false,
        label: 'Dedicated Private Travel Advisor & Concierge',
      },
    ],
    iconSrc: '/icons/subscriptions/free-plan.svg',
    iconSrcLarge: '/icons/subscriptions/free_large.svg',
    accentColor: 'bg-customBlue',
  },

  'Proficient Pioneer': {
    id: 2,
    price: { monthly: 50, yearly: 500, yearlyOriginal: 600 },
    info: [
      {
        highlighted: 'Optional $0-$25,000',
        normal: 'one time Minimum Credit Balance Deposit',
      },
      {
        normal: 'No Custom Charter Hourly Rate Discount',
      },
      {
        highlighted: '1%',
        normal: 'One-Way Charter Special Discount',
      },
    ],
    features: [
      { isAvailable: true, label: 'Full Access to SKYSHARE Platform' },
      { isAvailable: true, label: 'Full Access to Custom Charter' },
      { isAvailable: true, label: 'Full Access to One-Way Special Bookings' },
      { isAvailable: true, label: 'Create SKYSHARE GROUPS' },
      {
        isAvailable: true,
        label: 'Full-Service Private Travel Advisor Team & Concierge Services',
      },
      {
        isAvailable: false,
        label: 'Member Welcoming Kit',
      },
      {
        isAvailable: false,
        label: 'Member Card',
      },
      {
        isAvailable: false,
        label: 'Invites to Parties & Special Events',
      },
      {
        isAvailable: false,
        label: 'Invites to Exclusive Member Only Parties & Private Events',
      },
      {
        isAvailable: false,
        label: 'Exclusive SKYSHARE GROUPS',
      },
      {
        isAvailable: false,
        label: 'Dedicated Private Travel Advisor & Concierge',
      },
    ],
    iconSrc: '/icons/subscriptions/proficient-pioneer-plan.svg',
    iconSrcLarge: '/icons/subscriptions/pioneer_large.svg',
    accentColor: 'bg-proficientPioneerPlan',
  },

  'Legendary Explorer': {
    id: 3,
    price: { monthly: 165, yearly: 1500, yearlyOriginal: 1980 },
    info: [
      {
        highlighted: '$25,000',
        normal: 'one time Minimum Credit Balance Deposit',
      },
      {
        highlighted: '2.50%',
        normal: 'Custom Charter Hourly Rate Discount',
      },
      {
        highlighted: '2%',
        normal: 'One-Way Charter Special Discount',
      },
    ],
    features: [
      { isAvailable: true, label: 'Full Access to SKYSHARE Platform' },
      { isAvailable: true, label: 'Full Access to Custom Charter' },
      { isAvailable: true, label: 'Full Access to One-Way Special Bookings' },
      { isAvailable: true, label: 'Create SKYSHARE GROUPS' },
      {
        isAvailable: true,
        label: 'Full-Service Private Travel Advisor Team & Concierge Services',
      },
      {
        isAvailable: true,
        label: 'Member Welcoming Kit',
      },
      {
        isAvailable: true,
        label: 'Member Card',
      },
      {
        isAvailable: true,
        label: 'Invites to Parties & Special Events',
      },
      {
        isAvailable: false,
        label: 'Invites to Exclusive Member Only Parties & Private Events',
      },
      {
        isAvailable: false,
        label: 'Exclusive SKYSHARE GROUPS',
      },
      {
        isAvailable: false,
        label: 'Dedicated Private Travel Advisor & Concierge',
      },
    ],
    iconSrc: '/icons/subscriptions/legendary-explorer-plan.svg',
    iconSrcLarge: '/icons/subscriptions/explorer_large.svg',
    accentColor: 'bg-legendaryExplorerPlan',
    isMostPopular: true,
  },

  'Galactic Adventurer': {
    id: 4,
    price: { monthly: 350, yearly: 3500, yearlyOriginal: 4200 },
    info: [
      {
        highlighted: '$50,000',
        normal: 'one time Minimum Credit Balance Deposit',
      },
      {
        highlighted: '5.00%',
        normal: 'Custom Charter Hourly Rate Discount',
      },
      {
        highlighted: '3%',
        normal: 'One-Way Charter Special Discount',
      },
    ],
    features: [
      { isAvailable: true, label: 'Full Access to SKYSHARE Platform' },
      { isAvailable: true, label: 'Full Access to Custom Charter' },
      { isAvailable: true, label: 'Full Access to One-Way Special Bookings' },
      { isAvailable: true, label: 'Create SKYSHARE GROUPS' },
      {
        isAvailable: true,
        label: 'Full-Service Private Travel Advisor Team & Concierge Services',
      },
      {
        isAvailable: true,
        label: 'Member Welcoming Kit',
      },
      {
        isAvailable: true,
        label: 'Member Card',
      },
      {
        isAvailable: true,
        label: 'Invites to Parties & Special Events',
      },
      {
        isAvailable: true,
        label: 'Invites to Exclusive Member Only Parties & Private Events',
      },
      {
        isAvailable: true,
        label: 'Exclusive SKYSHARE GROUPS',
      },
      {
        isAvailable: false,
        label: 'Dedicated Private Travel Advisor & Concierge',
      },
    ],
    iconSrc: '/icons/subscriptions/galactic-adventurer-plan.svg',
    iconSrcLarge: '/icons/subscriptions/galactic_large.svg',
    accentColor: 'bg-galacticAdventurerPlan',
  },

  'Nexus Elite': {
    id: 5,
    price: { monthly: 500, yearly: 5000, yearlyOriginal: 6000 },
    info: [
      {
        highlighted: '$100,000',
        normal: 'one time Minimum Credit Balance Deposit',
      },
      {
        highlighted: '7.50%',
        normal: 'Custom Charter Hourly Rate Discount',
      },
      {
        highlighted: '4%',
        normal: 'One-Way Charter Special Discount',
      },
    ],
    features: [
      { isAvailable: true, label: 'Full Access to SKYSHARE Platform' },
      { isAvailable: true, label: 'Full Access to Custom Charter' },
      { isAvailable: true, label: 'Full Access to One-Way Special Bookings' },
      { isAvailable: true, label: 'Create SKYSHARE GROUPS' },
      {
        isAvailable: true,
        label: 'Full-Service Private Travel Advisor Team & Concierge Services',
      },
      {
        isAvailable: true,
        label: 'Member Welcoming Kit',
      },
      {
        isAvailable: true,
        label: 'Member Card',
      },
      {
        isAvailable: true,
        label: 'Invites to Parties & Special Events',
      },
      {
        isAvailable: true,
        label: 'Invites to Exclusive Member Only Parties & Private Events',
      },
      {
        isAvailable: true,
        label: 'Exclusive SKYSHARE GROUPS',
      },
      {
        isAvailable: true,
        label: 'Dedicated Private Travel Advisor & Concierge',
      },
    ],
    iconSrc: '/icons/subscriptions/nexus-elite-plan.svg',
    iconSrcLarge: '/icons/subscriptions/nexus_large.svg',
    accentColor: 'bg-nexusElitePlan',
  },
};
